import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RichTextEditor } from "@/components/RichTextEditor";
import { FileSignature, Plus, Loader2, ArrowLeft, Save, Trash2, Send, Archive } from "lucide-react";

const TEAL = "#2f6f6b";
const TONE = { Draft: "bg-amber-50 text-amber-700 border-amber-200", Published: "bg-emerald-50 text-emerald-700 border-emerald-200", Retired: "bg-slate-100 text-slate-500 border-slate-200" };
const PLACEHOLDERS = ["participant_name", "ndis_number", "participant_phone", "participant_email", "start_date", "end_date", "total_value", "representative_full_name", "sos_preview_data", "financial_summary_data"];

export default function ServiceAgreementTemplates() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null); // template object or {} for new

  const load = () => api.saTemplates().then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  if (editing) return <Editor tpl={editing} onBack={() => { setEditing(null); load(); }} />;

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="sa-templates-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2.5"><FileSignature className="h-5 w-5" style={{ color: TEAL }} /><h1 className="text-xl font-semibold font-brand text-slate-800">Service Agreement Templates</h1></div>
          <p className="text-xs text-slate-500 mt-1">The T&amp;C bodies used when generating Service Agreements. Publish a template to make it selectable; content locks once published.</p>
        </div>
        <Button size="sm" style={{ backgroundColor: TEAL }} onClick={() => setEditing({})} data-testid="sa-tpl-new"><Plus className="h-4 w-4 mr-1.5" />New template</Button>
      </div>

      <div className="mt-5 space-y-2">
        {list === null ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          : list.length === 0 ? <div className="text-center py-14 text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">No templates yet.</div>
          : list.map((t) => (
            <button key={t.id} onClick={() => setEditing(t)} data-testid={`sa-tpl-row-${t.id}`} className="w-full text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-[#2f6f6b] hover:shadow-sm transition-all flex items-center gap-3">
              <span className="flex-1 text-sm font-medium text-slate-800">{t.template_name}</span>
              <Badge className={`border ${TONE[t.status] || TONE.Draft}`}>{t.status}</Badge>
            </button>
          ))}
      </div>
    </div>
  );
}

function Editor({ tpl, onBack }) {
  const isNew = !tpl.id;
  const [name, setName] = useState(tpl.template_name || "");
  const [body, setBody] = useState(tpl.agreement_body || "");
  const [status, setStatus] = useState(tpl.status || "Draft");
  const [id, setId] = useState(tpl.id || null);
  const [busy, setBusy] = useState(false);
  const locked = status !== "Draft";

  const save = async () => {
    if (!name.trim()) { toast.error("Enter a template name."); return; }
    setBusy(true);
    try {
      if (id) { await api.saTemplateUpdate(id, { template_name: name, agreement_body: body }); }
      else { const d = await api.saTemplateCreate({ template_name: name, agreement_body: body }); setId(d.id); }
      toast.success("Saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };
  const transition = async (to) => {
    if (!id) { toast.error("Save the template first."); return; }
    if (!window.confirm(`Set this template to ${to}?${to === "Published" ? " Content will be locked." : ""}`)) return;
    try { await api.saTemplateStatus(id, to); setStatus(to); toast.success(`Template ${to}.`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const remove = async () => {
    if (!id || !window.confirm("Delete this draft template?")) return;
    try { await api.saTemplateDelete(id); toast.success("Deleted."); onBack(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed."); }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="sa-tpl-editor">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800" data-testid="sa-tpl-back"><ArrowLeft className="h-3.5 w-3.5" />Back to templates</button>
        <Badge className={`border ${TONE[status] || TONE.Draft}`}>{status}</Badge>
      </div>

      <div className="space-y-1 mb-3">
        <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Template name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} disabled={locked} className="h-9 text-sm" data-testid="sa-tpl-name" />
      </div>

      <div className="text-[11px] text-slate-500 mb-2">Placeholders (Jinja): {PLACEHOLDERS.map((p) => <code key={p} className="mx-0.5 px-1 py-0.5 bg-slate-100 rounded text-[10px]">{`{{ doc.${p} }}`}</code>)}</div>

      <div className="space-y-1 mb-4">
        <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Agreement body (T&amp;Cs)</Label>
        <RichTextEditor value={body} onChange={setBody} disabled={locked} testid="sa-tpl-body" placeholder="Write the terms & conditions. Use {{ doc.participant_name }} etc." />
      </div>

      <div className="flex items-center gap-2">
        {!locked && <Button size="sm" style={{ backgroundColor: TEAL }} onClick={save} disabled={busy} data-testid="sa-tpl-save">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}Save</Button>}
        {status === "Draft" && id && <Button size="sm" variant="outline" onClick={() => transition("Published")} data-testid="sa-tpl-publish"><Send className="h-4 w-4 mr-1.5" />Publish</Button>}
        {status !== "Retired" && id && <Button size="sm" variant="outline" onClick={() => transition("Retired")} data-testid="sa-tpl-retire"><Archive className="h-4 w-4 mr-1.5" />Retire</Button>}
        {status === "Draft" && id && <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-auto" onClick={remove} data-testid="sa-tpl-delete"><Trash2 className="h-4 w-4 mr-1.5" />Delete</Button>}
      </div>
      {locked && <p className="text-[11px] text-slate-400 mt-3">Content is locked because the template is {status}. Create a new template to make changes.</p>}
    </div>
  );
}
